import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CheckCircle,
  Package,
  Truck,
  ArrowRight,
  Calendar,
  Play,
  Bell,
  Share2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import confetti from 'canvas-confetti';

const OrderConfirmationPage = () => {
  const location = useLocation();
  const orderData = location.state || {
    orderId: 'ORD-DEMO-12345',
    total: 99.97,
    items: [],
    shippingMethod: { name: 'Standard Shipping', time: '5-7 business days' },
  };

  React.useEffect(() => {
    // Trigger confetti on page load
    if (typeof confetti === 'function') {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#dc2626', '#f87171', '#fca5a5'],
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50/30 to-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Success Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Order Confirmed!
          </h1>
          <p className="text-gray-600 text-lg">
            Thank you for your purchase. Your recovery journey continues!
          </p>
        </div>

        {/* Order Details Card */}
        <Card className="border-gray-100 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-gray-500">Order Number</p>
                <p className="text-xl font-bold text-gray-900">{orderData.orderId}</p>
              </div>
              <Button variant="outline" size="sm" className="border-gray-200">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>

            {/* Order Timeline Preview */}
            <div className="relative">
              <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-gray-200" />
              <div className="space-y-6">
                {[
                  { icon: CheckCircle, label: 'Order Placed', status: 'complete', time: 'Just now' },
                  { icon: Package, label: 'Processing', status: 'current', time: 'Est. today' },
                  { icon: Truck, label: 'Shipped', status: 'pending', time: orderData.shippingMethod?.time },
                  { icon: CheckCircle, label: 'Delivered', status: 'pending', time: '' },
                ].map((step, index) => (
                  <div key={index} className="flex items-center gap-4 relative">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                        step.status === 'complete'
                          ? 'bg-emerald-500 text-white'
                          : step.status === 'current'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <step.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${
                        step.status === 'pending' ? 'text-gray-400' : 'text-gray-900'
                      }`}>
                        {step.label}
                      </p>
                      {step.time && (
                        <p className="text-sm text-gray-500">{step.time}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Info */}
            <div className="mt-8 p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{orderData.shippingMethod?.name}</p>
                  <p className="text-sm text-gray-500">Estimated delivery: {orderData.shippingMethod?.time}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total */}
        <Card className="border-gray-100 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Order Total</span>
              <span className="text-2xl font-bold text-gray-900">${orderData.total?.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Post-Purchase Actions */}
        <div className="space-y-4 mb-8">
          <h2 className="text-lg font-bold text-gray-900">What's Next?</h2>
          
          <Card className="border-gray-100 hover:border-red-200 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                  <Play className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Product Onboarding</p>
                  <p className="text-sm text-gray-500">Learn how to use your new products effectively</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-100 hover:border-red-200 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Set Usage Reminders</p>
                  <p className="text-sm text-gray-500">Stay consistent with your rehab routine</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-100 hover:border-red-200 hover:shadow-md transition-all cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Bell className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Track Your Delivery</p>
                  <p className="text-sm text-gray-500">Get real-time updates on your order</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/orders" className="flex-1">
            <Button variant="outline" className="w-full h-12 rounded-full border-gray-200">
              <Package className="w-5 h-5 mr-2" />
              View My Orders
            </Button>
          </Link>
          <Link to="/shop" className="flex-1">
            <Button className="w-full bg-red-600 hover:bg-red-700 text-white h-12 rounded-full">
              Continue Shopping
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>

        {/* Support Note */}
        <p className="text-center text-sm text-gray-500 mt-8">
          Questions about your order? <a href="#" className="text-red-600 hover:underline">Contact Support</a>
        </p>
      </div>

      {/* Footer Spacer for mobile nav */}
      <div className="h-20 md:h-0" />
    </div>
  );
};

export default OrderConfirmationPage;